import React, { useEffect, useState } from "react";
import { FilePlus2, FileText, Pencil, Copy, Trash2, CalendarClock } from "lucide-react";
import { Button } from "../ui/button";
import { apiListContracts, apiSaveContract, apiDeleteContract } from "../../api";
import { medimarkTemplate, blankContract, uid } from "../../lib/contractTemplate";
import { toast } from "../../hooks/use-toast";
import ContractEditor from "./ContractEditor";
import { daysUntil } from "./PlatformPanel";

const RenewChip = ({ iso }) => {
  const d = daysUntil(iso);
  if (d === null) return null;
  const tone = d < 0 ? "bg-red-50 text-red-600" : d <= 15 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  const txt = d < 0 ? `Vencida hace ${-d} d` : d === 0 ? "Vence hoy" : `Renueva en ${d} d`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <CalendarClock className="h-3 w-3" /> {txt}
    </span>
  );
};

// Pestaña "Contratos": expediente por cliente, plantilla MediMark, editor y PDF.
const ContractsTab = ({ clients, clientFilter, setClientFilter }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    apiListContracts()
      .then(setList)
      .finally(() => setLoading(false));
  }, []);

  const create = async (base) => {
    const doc = { ...base, id: uid() + uid(), client_id: clientFilter || "" };
    const saved = await apiSaveContract(doc);
    setList((l) => [saved, ...l]);
    setEditing(saved);
  };
  const duplicate = async (c) => {
    const saved = await apiSaveContract({ ...c, id: uid() + uid(), name: `${c.name} (copia)`, renewals: [] });
    setList((l) => [saved, ...l]);
    toast({ title: "Contrato duplicado" });
  };
  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el contrato "${c.name}"? Esta acción no se puede deshacer.`)) return;
    await apiDeleteContract(c.id);
    setList((l) => l.filter((x) => x.id !== c.id));
  };

  const clientName = (id) => clients.find((x) => x.id === id)?.name;
  const shown = clientFilter ? list.filter((c) => c.client_id === clientFilter) : list;

  return (
    <div data-testid="contracts-tab">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Contratos</h2>
          <p className="text-sm text-slate-500">Edita cada contrato, descarga el PDF y lleva el registro de renovaciones de cada plataforma.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => create(medimarkTemplate())} className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="contract-new-template">
            <FilePlus2 className="h-4 w-4" /> Desde plantilla MediMark
          </Button>
          <Button variant="outline" onClick={() => create(blankContract())} className="gap-1.5" data-testid="contract-new-blank">
            <FileText className="h-4 w-4" /> En blanco
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-500">Expediente de:</span>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="contracts-client-filter">
          <option value="">Todos los clientes</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Cargando contratos…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 py-14 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">Aún no hay contratos{clientFilter ? " para este cliente" : ""}.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <div key={c.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="contract-card">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <FileText className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-900">{c.name}</p>
                  <p className="truncate text-xs text-slate-500">{clientName(c.client_id) || "Sin cliente asignado"}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                {c.platform?.name && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">{c.platform.name}</span>}
                {c.platform?.monthly_fee && <span>${Number(c.platform.monthly_fee).toLocaleString("es-MX")}/mes</span>}
                <RenewChip iso={c.platform?.renewal_date} />
                {(c.renewals || []).length > 0 && <span>· {c.renewals.length} renovación(es)</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => setEditing(c)} className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="contract-edit">
                  <Pencil className="h-4 w-4" /> Editar / PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(c)} title="Duplicar"><Copy className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(c)} title="Eliminar" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ContractEditor
          initial={editing}
          clients={clients}
          onClose={(updated) => {
            setList((l) => l.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default ContractsTab;
